import { load_explorer_swanbot_key } from './api.js';
import { syncServerRenderedState } from './state.js';
import { encodeAgentToken } from './agent_requests.mjs';
import {
  load_explorer_swanbot_agents,
  load_explorer_swanbot_agent_detail,
  load_swanbot_agent_run,
  create_dataexplorer_agent,
  update_swanbot_agent,
  update_dataexplorer_agent_run_state,
  delete_dataexplorer_agent,
} from './swanbot.js';
import {
  renderAgentChannels,
  renderAgentResult,
  escapeAgentHtml as escape,
} from './legacy_events.js';

function parseAgentDate(value) {
  const timestamp = String(value).replace(
    /^(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})$/, '$1T$2:$3:$4Z'
  );
  return new Date(timestamp);
}

function formatDate(value) {
  if (!value) return 'No activity yet';
  const date = parseAgentDate(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23', timeZone: 'UTC',
  }).format(date) + ' UTC';
}

function formatRunAge(value) {
  const date = parseAgentDate(value);
  if (Number.isNaN(date.getTime())) return 'Run time unavailable';
  const difference = Date.now() - date.getTime();
  let minutes = Math.floor(Math.abs(difference) / 60000);
  if (minutes === 0) return difference < 0 ? 'In less than a minute' : 'Just now';
  const parts = [];
  for (const [unit, size] of [['day', 1440], ['hour', 60], ['minute', 1]]) {
    const count = Math.floor(minutes / size);
    if (count) parts.push(count + ' ' + unit + (count === 1 ? '' : 's'));
    minutes %= size;
    if (parts.length === 2) break;
  }
  const duration = parts.join(' ');
  return difference < 0 ? 'In ' + duration : duration + ' ago';
}

function lastSuccessfulRun(agent) {
  const timestamp = agent.last_run || agent.last_run_at;
  if (!timestamp || Number.isNaN(parseAgentDate(timestamp).getTime())) {
    return 'Execution time unavailable';
  }
  return escape(formatDate(timestamp)) + '<small>' + escape(formatRunAge(timestamp)) + '</small>';
}

function statusBadge(status) {
  const normalized = String(status || 'Unknown').toLowerCase();
  const tone = ['monitoring', 'active', 'completed', 'success', 'idle'].includes(normalized) ? 'positive'
    : ['error', 'failed', 'blocked'].includes(normalized) ? 'negative'
      : ['running', 'warning'].includes(normalized) ? 'warning' : 'neutral';
  const label = normalized === 'disabled' ? 'Paused' : normalized.charAt(0).toUpperCase() + normalized.slice(1);
  return '<span class="agents-status agents-status-' + tone + '">' + escape(label) + '</span>';
}

function stat(label, value) {
  return '<div class="agents-stat"><dt>' + escape(label) + '</dt><dd>' + value + '</dd></div>';
}

function responseData(result) {
  if (!result || result.status !== 'success') {
    throw new Error(result?.msg || 'The agent request failed.');
  }
  return result;
}

function errorMessage(error) {
  if (error.response?.status === 404) return 'This agent or message is no longer available.';
  if (error.response?.status === 403) return 'You do not have access to this agent message.';
  const data = error.response?.data;
  return typeof data === 'string' ? data : data?.detail || data?.msg || error.message || 'Unable to load agents.';
}

export async function initializeAgentsPage(root) {
  const isAdmin = root.dataset.role === 'admin';
  const adminOnlyIcon = '<i class="fas fa-user-shield agents-admin-icon" role="img" aria-label="Admin only" title="Admin only"></i>';
  let selected = null;
  let selection = 0;
  let messageRequest = 0;
  let selectedAgentId = root.dataset.agent || '';
  const feedback = root.querySelector('#agents-feedback');
  const list = root.querySelector('#agents-list');
  const panel = root.querySelector('#agents-detail');
  const editor = root.querySelector('#agents-editor');
  const create = root.querySelector('#agents-create');
  const overview = root.querySelector('#agents-overview');
  const main = root.querySelector('#agents-main');
  const back = root.querySelector('#agents-back');
  const listView = root.querySelector('#agents-view-list');
  const gridView = root.querySelector('#agents-view-grid');
  function setListView(view) {
    list.dataset.view = view;
    listView.setAttribute('aria-pressed', String(view === 'list'));
    gridView.setAttribute('aria-pressed', String(view === 'grid'));
  }
  listView.onclick = () => setListView('list');
  gridView.onclick = () => setListView('grid');
  setListView('list');
  const current = () => root.isConnected;
  const showError = (error) => {
    console.error('Agents request failed', error);
    feedback.textContent = errorMessage(error);
    feedback.className = 'alert alert-danger';
  };
  const clearFeedback = () => { feedback.className = 'd-none'; feedback.textContent = ''; };
  const markSelectedAgent = () => {
    list.querySelectorAll('[data-agent-id]').forEach(button => {
      button.setAttribute('aria-current', String(button.dataset.agentId === selectedAgentId));
    });
  };

  async function refreshList() {
    const result = responseData(await load_explorer_swanbot_agents());
    if (!current()) return;
    if (!Array.isArray(result.agents)) throw new Error('The server returned an invalid agent list.');
    list.replaceChildren();
    if (!result.agents.length) {
      list.innerHTML = '<p class="agents-list-empty">' + (isAdmin
        ? 'No agents yet. Create an agent to get started.'
        : 'Chat with <a href="/dataexplorer">Swanbot</a> to create one.') + '</p>';
      return;
    }
    for (const agent of result.agents) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'agents-list-item';
      button.dataset.agentId = agent.id || agent.agent_id;
      button.innerHTML = '<span class="agents-list-title">' + escape(agent.name || agent.id || agent.agent_id) + '</span>'
        + statusBadge(agent.enabled === false ? 'paused' : agent.status)
        + '<span class="agents-list-meta">Last successful run<br>' + lastSuccessfulRun(agent) + '</span>';
      button.addEventListener('click', () => selectAgent(agent.id || agent.agent_id));
      list.appendChild(button);
    }
    markSelectedAgent();
  }

  function updateUrl(agentId) {
    const query = new URLSearchParams({ customer: root.dataset.organization });
    if (agentId) query.set('agent', agentId);
    window.history.pushState(null, '', '/agents?' + query);
  }

  function showDetailView() {
    overview.hidden = true;
    main.hidden = false;
    back.hidden = false;
  }

  function showOverview() {
    const previousAgentId = selectedAgentId;
    ++selection;
    ++messageRequest;
    selected = null;
    selectedAgentId = '';
    markSelectedAgent();
    clearFeedback();
    editor.replaceChildren();
    panel.replaceChildren();
    panel.hidden = false;
    overview.hidden = false;
    main.hidden = true;
    back.hidden = true;
    updateUrl('');
    const previousAgent = [...list.querySelectorAll('[data-agent-id]')]
      .find(button => button.dataset.agentId === previousAgentId);
    (previousAgent || create)?.focus();
  }

  back.onclick = showOverview;

  async function showRun(agentId, runId, kind, target, requestSelection) {
    const request = ++messageRequest;
    target.className = 'agents-well agents-message';
    target.innerHTML = '<div class="agents-loading" role="status"><i class="fas fa-circle-notch fa-spin me-2" aria-hidden="true"></i>Loading stored message...</div>';
    panel.querySelectorAll('[data-run-id]').forEach(button => {
      button.setAttribute('aria-current', String(button.dataset.runId === runId));
    });
    try {
      const result = responseData(await load_swanbot_agent_run(agentId, runId, kind));
      if (!current() || requestSelection !== selection || request !== messageRequest) return;
      if (!result.run || typeof result.run.message_markdown !== 'string') {
        throw new Error('The server returned an invalid agent message.');
      }
      target.innerHTML = '<div class="agents-message-heading"><div>'
        + '<span class="agents-eyebrow">SELECTED RESULT</span><h4>Agent generated message</h4>'
        + '<div class="agents-message-date">Data through ' + escape(formatDate(result.run.run_id)) + '</div></div>'
        + '</div>'
        + '<div class="agents-message-body">' + renderAgentResult({ summary_markdown: result.run.message_markdown }) + '</div>';
    } catch (error) {
      if (!current() || requestSelection !== selection || request !== messageRequest) return;
      target.textContent = errorMessage(error);
      target.className = 'alert alert-danger';
      console.error('Agent message request failed', error);
    }
  }

  async function selectAgent(agentId, runId = '', kind = 'execution', navigate = true) {
    const requestSelection = ++selection;
    selected = null;
    selectedAgentId = agentId;
    markSelectedAgent();
    clearFeedback();
    editor.replaceChildren();
    showDetailView();
    panel.hidden = false;
    panel.className = 'agents-detail';
    panel.innerHTML = '<div class="agents-well agents-loading" role="status"><i class="fas fa-circle-notch fa-spin me-2" aria-hidden="true"></i>Loading agent...</div>';
    if (navigate) {
      updateUrl(agentId);
      back.focus();
    }
    try {
      const result = responseData(await load_explorer_swanbot_agent_detail(agentId));
      if (!current() || requestSelection !== selection) return;
      if (!result.agent) throw new Error('The server returned an invalid agent.');
      selected = result.agent;
      const paused = selected.enabled === false || selected.configuration?.enabled === false
        || selected.status === 'paused' || selected.status_field === 'disabled';
      const events = Array.isArray(selected.events) ? selected.events : [];
      panel.innerHTML = '<section class="agents-well agents-summary"><div class="agents-agent-heading"><div>'
        + '<span class="agents-eyebrow">MONITORING AGENT</span><h3>' + escape(selected.name || selected.id) + '</h3>'
        + '<p class="agents-agent-id">' + escape(selected.id || agentId) + '</p></div>'
        + '<div class="agents-actions">'
        + (isAdmin
          ? '<button class="btn btn-primary" data-action="edit" title="Edit agent (admin only)"><i class="fas fa-pen me-1" aria-hidden="true"></i> Edit' + adminOnlyIcon + '</button>'
          : '')
        + '<button type="button" class="btn btn-secondary" data-action="notifications" aria-controls="agents-delivery" aria-expanded="false">'
        + '<i class="fas fa-bell me-1" aria-hidden="true"></i> Notifications</button>'
        + '<button class="btn btn-secondary" data-action="pause">' + (paused ? 'Resume' : 'Pause') + '</button>'
        + (isAdmin
          ? '<button class="btn agents-delete" data-action="delete" title="Delete agent (admin only)">Delete' + adminOnlyIcon + '</button>'
          : '')
        + '</div></div>'
        + '<dl class="agents-stats">'
        + stat('Status', statusBadge(paused ? 'paused' : selected.status))
        + stat('Last successful run', lastSuccessfulRun(selected))
        + stat('Total runs', escape(String(selected.recent_event_count ?? events.length)))
        + '</dl></section>'
        + '<section id="agents-delivery" class="agents-well agents-config" aria-labelledby="agents-delivery-heading" hidden>'
        + '<div class="agents-well-heading"><div><span class="agents-eyebrow">NOTIFICATION CHANNELS</span>'
        + '<h4 id="agents-delivery-heading">Delivery</h4></div></div>'
        + renderAgentChannels(selected.notification_channels || []) + '</section>'
        + '<div class="agents-run-workspace">'
        + '<aside class="agents-well agents-runs" aria-label="Agent runs"><div class="agents-well-heading"><div>'
        + '<span class="agents-eyebrow">AGENT RESULTS</span><h4>LATEST 10 RESULTS</h4></div>'
        + '<span class="agents-count">' + events.length + '</span></div>'
        + '<div id="agents-history" class="agents-history"></div></aside>'
        + '<div class="agents-run-detail">'
        + '<section id="agents-message" class="agents-well agents-empty"><h4>No messages yet</h4>'
        + '<p>Results will appear here after this agent runs.</p></section></div></div>'
        + (isAdmin
          ? '<dialog id="agents-delete-dialog" class="agents-delete-dialog" aria-labelledby="agents-delete-title" aria-describedby="agents-delete-warning">'
            + '<h4 id="agents-delete-title">Delete agent?</h4>'
            + '<p id="agents-delete-warning">After deletion, historical run data will no longer be available and existing message links will stop working. This cannot be undone.</p>'
            + '<div class="agents-editor-actions"><button type="button" class="btn btn-secondary" data-delete-cancel autofocus>Cancel</button>'
            + '<button type="button" class="btn btn-danger" data-delete-confirm>Delete agent</button></div></dialog>'
          : '');
      const delivery = panel.querySelector('#agents-delivery');
      const notifications = panel.querySelector('[data-action="notifications"]');
      notifications.onclick = () => {
        delivery.hidden = !delivery.hidden;
        notifications.setAttribute('aria-expanded', String(!delivery.hidden));
      };
      if (isAdmin) {
        panel.querySelector('[data-action="edit"]').onclick = () => showEditor('edit');
        const deleteButton = panel.querySelector('[data-action="delete"]');
        const deleteDialog = panel.querySelector('#agents-delete-dialog');
        deleteButton.onclick = () => deleteDialog.showModal();
        deleteDialog.onkeydown = event => {
          if (event.key === 'Escape') {
            event.preventDefault();
            deleteDialog.close();
          }
        };
        deleteDialog.onclose = () => {
          if (current() && requestSelection === selection) deleteButton.focus();
        };
        panel.querySelector('[data-delete-cancel]').onclick = () => deleteDialog.close();
        panel.querySelector('[data-delete-confirm]').onclick = () => {
          if (!deleteDialog.open || !current() || requestSelection !== selection) return;
          deleteDialog.close();
          return mutate(() => delete_dataexplorer_agent(agentId), '');
        };
      }
      panel.querySelector('[data-action="pause"]').onclick = () => mutate(
        () => update_dataexplorer_agent_run_state(agentId, paused), agentId
      );
      const history = panel.querySelector('#agents-history');
      for (const event of events) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'agents-history-item';
        button.dataset.runId = event.run_id || event.ran_at;
        const runDate = event.run_id || event.ran_at;
        button.innerHTML = '<span class="agents-history-date"><small>Data Through</small>'
          + '<span>' + escape(formatDate(runDate)) + '</span></span>'
          + statusBadge(event.status);
        button.onclick = () => {
          const timestamp = event.run_id || event.ran_at;
          const token = encodeAgentToken(root.dataset.organization, root.dataset.user, agentId, timestamp);
          window.history.pushState(null, '', '/agents/' + token);
          showRun(agentId, timestamp, 'execution', panel.querySelector('#agents-message'), requestSelection);
        };
        history.appendChild(button);
      }
      if (!events.length) history.textContent = 'This agent has no stored executions yet.';
      const initialRunId = runId || events[0]?.run_id || events[0]?.ran_at;
      if (initialRunId) {
        await showRun(agentId, initialRunId, runId ? kind : 'execution', panel.querySelector('#agents-message'), requestSelection);
      }
    } catch (error) {
      if (!current() || requestSelection !== selection) return;
      panel.textContent = 'Unable to load this agent.';
      showError(error);
    }
  }

  async function mutate(operation, agentId) {
    const buttons = [...root.querySelectorAll('button')];
    buttons.forEach(button => { button.disabled = true; });
    clearFeedback();
    try {
      const result = responseData(await operation());
      if (!current()) return;
      editor.replaceChildren();
      await refreshList();
      const targetAgentId = agentId === undefined ? result.agent?.agent_id || result.agent?.id : agentId;
      if (targetAgentId) {
        await selectAgent(targetAgentId);
      } else if (agentId === '') {
        showOverview();
        feedback.className = 'alert alert-success';
        feedback.textContent = 'Agent deleted.';
      } else {
        throw new Error('The server did not return the saved agent identifier.');
      }
    } catch (error) {
      if (current()) showError(error);
    } finally {
      buttons.forEach(button => { button.disabled = false; });
    }
  }

  function showEditor(mode) {
    if (!isAdmin) {
      showError(new Error('Only admins can create or edit agents.'));
      return;
    }
    const editing = mode === 'edit';
    const existing = editing ? selected : null;
    if (mode === 'create') {
      ++selection;
      ++messageRequest;
      selected = null;
      selectedAgentId = '';
      markSelectedAgent();
      panel.replaceChildren();
      panel.hidden = true;
    }
    showDetailView();
    editor.innerHTML = '<form class="agents-well agents-editor">'
      + '<div class="agents-well-heading"><div><span class="agents-eyebrow">AGENT SETUP</span><h4>'
      + (editing ? 'Edit Agent' : 'Create Agent') + '</h4></div></div>'
      + '<label class="form-label" for="agents-description">Description</label>'
      + '<input class="form-control mb-3" id="agents-description" name="description" required>'
      + '<label class="form-label" for="agents-prompt">Prompt</label>'
      + '<textarea class="form-control mb-3" id="agents-prompt" name="prompt" rows="6" required></textarea>'
      + '<div class="agents-editor-actions"><button class="btn btn-secondary" type="button" data-cancel>Cancel</button>'
      + '<button class="btn btn-primary" type="submit">Save Agent</button></div></form>';
    const form = editor.querySelector('form');
    form.elements.description.value = existing?.name || '';
    form.elements.prompt.value = existing?.configuration?.objective || '';
    editor.querySelector('[data-cancel]').onclick = () => {
      if (mode === 'create') showOverview();
      else editor.replaceChildren();
    };
    form.onsubmit = async (event) => {
      event.preventDefault();
      const payload = { description: form.elements.description.value.trim(), prompt: form.elements.prompt.value.trim() };
      if (!payload.description || !payload.prompt) {
        showError(new Error('Prompt and description are required.'));
        return;
      }
      await mutate(
        () => editing ? update_swanbot_agent(existing.id, payload) : create_dataexplorer_agent(payload),
        editing ? existing.id : undefined
      );
    };
  }

  if (create && isAdmin) {
    create.onclick = () => showEditor('create');
    create.disabled = true;
  }
  try {
    syncServerRenderedState();
    window.WingmanState.swanbot_key = '';
    await load_explorer_swanbot_key();
    if (!current()) return;
    if (!window.WingmanState.swanbot_relay_enabled && !window.WingmanState.swanbot_key) {
      throw new Error('Unable to authenticate agent requests. Sign in again and retry.');
    }
    if (create && isAdmin) create.disabled = false;
    await Promise.all([
      refreshList(),
      root.dataset.agent
        ? selectAgent(root.dataset.agent, root.dataset.run, root.dataset.messageKind, false)
        : Promise.resolve(),
    ]);
  } catch (error) {
    if (current()) {
      list.textContent = 'Unable to load agents.';
      showError(error);
    }
  }
}

export function observeAgentsPage() {
  const initialize = () => {
    const root = document.getElementById('agents-page');
    if (root && root.dataset.initialized !== 'true') {
      root.dataset.initialized = 'true';
      initializeAgentsPage(root);
    }
  };
  new MutationObserver(initialize).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('popstate', () => {
    if (document.getElementById('agents-page')) window.location.reload();
  });
  initialize();
}
